import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import {
  StatusCodeFilter,
  statusCodeIsPinned,
} from "../statusCodeFilter";
import { SPEED_UNITS } from "../../admin-details/account/constants";
import { convertKmphTo } from "../../../utils/distance";
import { TimedPathPoint } from "./resolveRoutePath";
import {
  EventPinFocus,
  eventMarkerIcon,
  PIN_Z_INDEX_BY_FOCUS,
} from "./eventIcons";
import { createSelectedEventPulse } from "./eventPinFocus";
import { createEventStackController } from "./eventStackController";
import { groupIntoRuns, runLabelIcon } from "./eventStacks";
import { formatDuration, isMovingEvent } from "../util/tripSummary";
import { createClusterAssetListOverlay } from "./clusterAssetListOverlay";
import { createClusterLensOverlay, LensTrip } from "./clusterLensOverlay";
import { detectOverlapClusters, OverlapCluster } from "./overlapClusters";
import { TripPointClickSource } from "./renderMultiVehicleRoutes";
import { buildReplayTimeline } from "./replayTimeline";
import { RouteLineStyle } from "./routeLineStyle";
import { TooltipController } from "./tooltip";
import { createRouteHitLayer } from "./routeHitArea";
import { createEventCamera } from "./eventCamera";

export type { TripPointClickSource };

export type SelectedDayTrip = {
  dateKey: string;
  dayIndex: number;
  dayLabel: string;
  // position in the day's trips - the trip's identity, and what its key is
  // built from. Distinct from tripNumber, which is only the label: a day
  // with heartbeats in it numbers its trips 1,2,3 while they sit at
  // indices 1,3,4.
  tripIndex: number;
  // what the user is shown - "Trip 2". Never used to find anything.
  tripNumber: number;
  color: string;
  points: VehicleMapDataPoint[];
  // the road-aligned path, once the route view has resolved one. absent
  // until then - the route draws on the raw fixes and is swapped in place
  // when this arrives, so the map is never held back waiting on Google.
  path?: TimedPathPoint[];
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// a week of trips can carry tens of thousands of fixes - Mesa reports every
// 5 seconds - and every animation frame re-sets the whole path on every
// route that moved. so the animation runs on a thinned copy and snaps to
// the real path once it lands. the eye can't tell at map zoom; the finished
// route is still exact.
const ANIMATION_MAX_POINTS = 400;

// A trip carrying this many event pins is treated as "heavy": its pins are
// built a chunk per frame instead of in one blocking pass, and they arrive
// as a travelling wave instead of every marker animating at once. Below it
// nothing changes - a normal trip keeps the single pass and the simultaneous
// drop-in, which is what makes it feel snappy.
const HEAVY_TRIP_PIN_COUNT = 400;
// markers constructed per frame while the route is drawing
const PIN_BUILD_CHUNK = 150;
// pins brought onto the map per frame once they exist
const PIN_REVEAL_CHUNK = 40;

const thinForAnimation = <T,>(items: T[]): T[] => {
  if (items.length <= ANIMATION_MAX_POINTS) return items;
  const stride = items.length / ANIMATION_MAX_POINTS;
  const thinned: T[] = [];
  for (let i = 0; i < ANIMATION_MAX_POINTS; i++)
    thinned.push(items[Math.floor(i * stride)]);
  // the real last fix, so a route ends where it actually ended
  const last = items[items.length - 1];
  if (thinned[thinned.length - 1] !== last) thinned.push(last);
  return thinned;
};

// a drawn route has three index-aligned forms: the full path, a thinned
// copy the clock-paced draws walk, and that copy's timestamps. Built from
// the road-aligned path where there is one, and the raw fixes otherwise.
const pathPartsFrom = (
  points: VehicleMapDataPoint[],
  path?: TimedPathPoint[]
) => {
  if (path && path.length >= 2) {
    const animPoints = thinForAnimation(path);
    return {
      rawPath: path.map((point) => ({ lat: point.lat, lng: point.lng })),
      animPath: animPoints.map((point) => ({ lat: point.lat, lng: point.lng })),
      animTimestamps: animPoints.map((point) => point.timestamp),
    };
  }
  const animPoints = thinForAnimation(points);
  return {
    rawPath: points.map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
    })),
    animPath: animPoints.map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
    })),
    animTimestamps: animPoints.map((point) => point.timestamp),
  };
};

const tripKeyFor = (trip: SelectedDayTrip) =>
  `${trip.dateKey}__${trip.tripIndex}`;

// mirrors polylineConfigLineStyles so these match the single-day Route View
// styles. snap-to-roads/directions are approximated visually on the raw GPS
// path - no Roads or Directions API calls.
const buildStrokeOptions = (style: RouteLineStyle) => {
  switch (style) {
    case "dashed":
      return {
        strokeOpacity: 0,
        strokeWeight: 2,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 2 },
            offset: "0",
            repeat: "10px",
          },
        ],
      };
    case "dotted":
      return {
        strokeOpacity: 0,
        strokeWeight: 2,
        icons: [
          {
            icon: { path: "M 0,0 0,0", strokeOpacity: 1, scale: 3 },
            offset: "0",
            repeat: "5px",
          },
        ],
      };
    case "solid_thin":
      return { strokeOpacity: 0.85, strokeWeight: 1.5, icons: [] as any[] };
    case "solid":
    default:
      return { strokeOpacity: 0.85, strokeWeight: 2.5, icons: [] as any[] };
  }
};

const eventLabel = (point: VehicleMapDataPoint) =>
  point.resolvedDescription ||
  point.status?.description ||
  `Status ${point.statuscode}`;

const speedUnitSuffix = (unit: SPEED_UNITS) => {
  switch (unit) {
    case SPEED_UNITS.MPH:
      return "mph";
    case SPEED_UNITS.NAUTICAL_MILES:
      return "kn";
    case SPEED_UNITS.KMH:
    default:
      return "km/h";
  }
};

const tooltipHtmlFor = (
  dayLabel: string,
  tripNumber: number,
  point: VehicleMapDataPoint,
  speedUnit: SPEED_UNITS
) => `
  <div><strong>${dayLabel} · Trip ${tripNumber}</strong></div>
  <div>${eventLabel(point)}</div>
  <div>${Math.round(convertKmphTo(speedUnit, point.speedkph))} ${speedUnitSuffix(speedUnit)}</div>
`;

const findNearestPoint = (
  points: VehicleMapDataPoint[],
  lat: number,
  lng: number
) => {
  let nearest = points[0];
  let nearestDistSq = Infinity;
  points.forEach((point) => {
    const dLat = point.latitude - lat;
    const dLng = point.longitude - lng;
    const distSq = dLat * dLat + dLng * dLng;
    if (distSq < nearestDistSq) {
      nearestDistSq = distSq;
      nearest = point;
    }
  });
  return nearest;
};

// One hue rather than a traffic-light scale: the map is already carrying a
// colour per day, and a second colour code fighting it reads as noise.
// size and depth instead - a busier junction is bigger and darker.
const CLUSTER_HUE = { h: 280, s: 62 };
const CLUSTER_MIN_DIAMETER = 24;
const CLUSTER_MAX_DIAMETER = 42;
// the count at which a badge is as large and as dark as it gets
const CLUSTER_SATURATION_COUNT = 8;

// How far one badge's stretch of shared road may run, expressed in screen
// terms rather than metres. What counts as one place is whatever the current
// view calls one: a corridor across a metro when zoomed out, a single
// junction at street level. Deriving the span from the zoom is what lets a
// badge split into the several it was standing for as you go in, instead of
// one number parked on one point at every scale.
// There is deliberately no ceiling on this. The zoom term IS the bound -
// it already holds a badge to OVERLAP_SPAN_PIXELS of screen whatever the
// scale - so a metres cap on top of it can only ever fire by overriding
// the span the view asked for. A 50km one used to, from zoom 10 down: at
// zoom 4 a pixel is 7.7km, so 420px wants a 3,239km span and the cap gave
// 50km - a badge every 6 pixels, which carpeted a cross-country fleet in
// thousands of them.
const OVERLAP_SPAN_PIXELS = 420;
const OVERLAP_SPAN_MIN_METERS = 150;

const metersPerPixelAt = (zoom: number, latitude: number) =>
  (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom);

const overlapSpanForZoom = (zoom: number, latitude: number) =>
  Math.max(
    OVERLAP_SPAN_MIN_METERS,
    metersPerPixelAt(zoom, latitude) * OVERLAP_SPAN_PIXELS
  );

const clusterBadgeIcon = (google: any, count: number) => {
  const weight = Math.min(
    1,
    (count - 2) / Math.max(1, CLUSTER_SATURATION_COUNT - 2)
  );
  const diameter = Math.round(
    CLUSTER_MIN_DIAMETER +
      (CLUSTER_MAX_DIAMETER - CLUSTER_MIN_DIAMETER) * weight
  );
  // lighter for a pair, deepening as more trips pile onto the same road
  const lightness = Math.round(62 - 26 * weight);
  const fill = `hsl(${CLUSTER_HUE.h} ${CLUSTER_HUE.s}% ${lightness}%)`;
  const halo = `hsl(${CLUSTER_HUE.h} ${CLUSTER_HUE.s}% ${lightness}% / 0.25)`;
  const center = diameter / 2;
  const fontSize = Math.round(diameter * 0.42);

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}">
      <circle cx="${center}" cy="${center}" r="${center - 0.5}" fill="${halo}" />
      <circle cx="${center}" cy="${center}" r="${center - 4}" fill="${fill}" stroke="white" stroke-width="2" />
      <text x="${center}" y="${center + fontSize * 0.35}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="white" text-anchor="middle">${count}</text>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(diameter, diameter),
    anchor: new google.maps.Point(center, center),
  };
};

// draws every checked trip as a day-coloured route on its real GPS path.
// where trips overlap, a cluster badge shows the count once the draw-in
// finishes; clicking it opens a zoomed lens with the routes nudged apart.
// the nudge only exists inside the lens - the main map is never altered.
export const renderMultiDayRoutes = ({
  map,
  trips,
  lineStyle,
  tooltip,
  speedUnit = SPEED_UNITS.KMH,
  timezone,
  onHoverTrip,
  onTripPointClick,
}: {
  map: any;
  trips: SelectedDayTrip[];
  lineStyle: RouteLineStyle;
  tooltip: TooltipController;
  // the account's configured speed unit - the hover tooltip's only unit
  // conversion, so it matches every other speed shown in the app
  speedUnit?: SPEED_UNITS;
  // the account's timezone, for the times on the stacked-event list
  timezone?: string;
  onHoverTrip?: (tripKey: string | null) => void;
  // clicking a route snaps to that trip's nearest event point. `source` is
  // what was actually clicked: a route click is a request to open that
  // trip, an event pin click is a request to read one event on a trip that
  // is already open, and must not disturb what is on the map around it.
  onTripPointClick?: (
    tripKey: string,
    point: VehicleMapDataPoint,
    source?: TripPointClickSource
  ) => void;
}) => {
  const google = (window as any).google;
  let cancelled = false;

  // shared between Replay All and the detail-view draw - only one ever runs
  // at a time, so one gate covers both. holds the loop between steps rather
  // than tearing anything down, so resuming picks up where it left off.
  let isReplayPaused = false;
  let replayResumeWaiters: (() => void)[] = [];
  const waitIfReplayPaused = () => {
    if (!isReplayPaused) return Promise.resolve();
    return new Promise<void>((resolve) => replayResumeWaiters.push(resolve));
  };
  const pauseReplay = () => {
    isReplayPaused = true;
  };
  const resumeReplay = () => {
    if (!isReplayPaused) return;
    isReplayPaused = false;
    const waiters = replayResumeWaiters;
    replayResumeWaiters = [];
    waiters.forEach((resolve) => resolve());
  };
  // an indefinitely-paused loop would otherwise never see a cancel flag
  // change, so anything that ends a run also releases the pause
  const releaseReplayPause = () => {
    isReplayPaused = false;
    const waiters = replayResumeWaiters;
    replayResumeWaiters = [];
    waiters.forEach((resolve) => resolve());
  };

  let currentLineStyle = lineStyle;
  let strokeIcons = buildStrokeOptions(currentLineStyle).icons;

  const routeHitLayer = createRouteHitLayer({ map });

  const entries = trips
    .filter((trip) => trip.points.length >= 2)
    .map((trip) => {
      const { rawPath, animPath, animTimestamps } = pathPartsFrom(
        trip.points,
        trip.path
      );

      const strokeOptions = buildStrokeOptions(lineStyle);
      const tripKey = tripKeyFor(trip);

      // the cursor is caught by a wide transparent line paired with this
      // one, and the layer hands the event to whichever route is nearest -
      // see routeHitArea
      const polyline = routeHitLayer.createRouteLine({
        tripKey,
        visibleOptions: {
          path: [],
          map,
          strokeColor: trip.color,
          strokeOpacity: strokeOptions.strokeOpacity,
          strokeWeight: strokeOptions.strokeWeight,
          icons: strokeOptions.icons,
        },
        handlers: {
          onEnter: () => {
            // Deliberately does NOT close the cluster list. Reaching that
            // popup means dragging the cursor off the badge and across
            // whatever lies between, and on a busy day that is always a
            // route - so closing on hover made the list unreachable, it
            // vanished on the way to it. A click is the signal that you
            // have moved on; see onClick.
            onHoverTrip?.(tripKey);
          },
          onMove: (point, domEvent) => {
            const nearest = findNearestPoint(
              trip.points,
              point.lat,
              point.lng
            );
            tooltip.show(
              tooltipHtmlFor(
                trip.dayLabel,
                trip.tripNumber,
                nearest,
                speedUnit
              ),
              domEvent.clientX,
              domEvent.clientY
            );
          },
          onLeave: () => {
            tooltip.hide();
            onHoverTrip?.(null);
          },
          onClick: (point) => {
            // picking a route is leaving the badge behind
            closeLens();
            const nearest = findNearestPoint(
              trip.points,
              point.lat,
              point.lng
            );
            onTripPointClick?.(tripKey, nearest, "route");
          },
        },
      });

      // index-aligned: animPath[i] is where the asset was at
      // animTimestamps[i]. the clock-paced draws below walk these, so the
      // head only ever sits on a real reported position.
      return {
        tripKey,
        trip,
        polyline,
        rawPath,
        animPath,
        animTimestamps,
        color: trip.color,
      };
    });

  const entryByTripKey = new Map(entries.map((entry) => [entry.tripKey, entry]));

  // true while a clock-paced draw is walking the thinned path. Swapping a
  // route's geometry underneath a running draw makes it jump, so upgrades
  // that land mid-animation are held and flushed when it finishes.
  let isAnimating = false;

  // Routes are drawn from the raw GPS fixes the moment the data lands, then
  // each one is swapped for its road-aligned path as that comes back. The
  // alternative - holding the map back until every trip has been through
  // snap-to-road and directions - is minutes at a week's scale.
  const applyPathUpgrade = (tripKey: string, path: TimedPathPoint[]) => {
    const entry = entryByTripKey.get(tripKey);
    if (!entry || path.length < 2) return;

    const parts = pathPartsFrom(entry.trip.points, path);
    entry.rawPath = parts.rawPath;
    entry.animPath = parts.animPath;
    entry.animTimestamps = parts.animTimestamps;
    entry.trip.path = path;
    entry.polyline.setPath(entry.rawPath);
  };

  const pendingPathUpgrades = new Map<string, TimedPathPoint[]>();

  const flushPendingPathUpgrades = () => {
    pendingPathUpgrades.forEach((path, tripKey) =>
      applyPathUpgrade(tripKey, path)
    );
    pendingPathUpgrades.clear();
  };

  // `immediate` is a route-view switch rather than a background upgrade.
  // A snap-to-road path landing mid-draw is held so the animation does not
  // jump, but a switch has already restyled the line, and holding the
  // geometry back leaves it drawn on the view the user just turned off.
  const updateTripPath = (
    tripKey: string,
    path: TimedPathPoint[],
    immediate = false
  ) => {
    if (isAnimating && !immediate) {
      pendingPathUpgrades.set(tripKey, path);
      return;
    }
    applyPathUpgrade(tripKey, path);
  };

  // in detail view the map is effectively single-trip: the other routes
  // fade to a context layer and stop responding to hover/click
  let detailModeTripKey: string | null = null;

  const HOVER_DIM_FACTOR = 0.15;
  const DETAIL_DIM_FACTOR = 0.1;

  // remembered so styling can be re-applied when detail mode changes -
  // setHighlightedTrip and setDetailedTrip can run in either order
  let highlightedTripKey: string | null = null;
  const hiddenTripKeys = new Set<string>();

  const applyRouteStyling = () => {
    const hoveredTripKey = highlightedTripKey;
    const baseOptions = buildStrokeOptions(currentLineStyle);
    const inDetailMode = detailModeTripKey !== null;
    entries.forEach((entry) => {
      const isHovered = entry.tripKey === hoveredTripKey;
      const isDimmed = hoveredTripKey !== null && !isHovered;
      const dimFactor = isDimmed
        ? inDetailMode
          ? DETAIL_DIM_FACTOR
          : HOVER_DIM_FACTOR
        : 1;
      const hidden =
        detailModeTripKey !== null && entry.tripKey !== detailModeTripKey;
      if (hidden !== hiddenTripKeys.has(entry.tripKey)) {
        entry.polyline.setMap(hidden ? null : map);
        if (hidden) hiddenTripKeys.add(entry.tripKey);
        else hiddenTripKeys.delete(entry.tripKey);
      }
      const weight = isHovered
        ? baseOptions.strokeWeight + 2
        : baseOptions.strokeWeight;
      // in detail view only the focused trip stays interactive
      const clickable = !inDetailMode || isHovered;

      // Both properties go on every time. Setting only one leaves the other
      // behind from the previous style: switching to dashed or dotted used
      // to add the symbols but leave the line underneath still opaque, so
      // the route stayed solid, and switching back left the old symbols
      // sitting on the solid line.
      const hasIcons = baseOptions.icons.length > 0;
      entry.polyline.setOptions({
        strokeWeight: weight,
        // an icon style hides the line itself - the symbols carry the colour
        strokeOpacity: hasIcons ? 0 : baseOptions.strokeOpacity * dimFactor,
        zIndex: isHovered ? 150 : 1,
        clickable,
        icons: hasIcons
          ? baseOptions.icons.map((iconDef) => ({
              ...iconDef,
              icon: {
                ...iconDef.icon,
                strokeOpacity: (iconDef.icon.strokeOpacity ?? 1) * dimFactor,
              },
            }))
          : [],
      });
    });
  };

  const setHighlightedTrip = (hoveredTripKey: string | null) => {
    highlightedTripKey = hoveredTripKey;
    applyRouteStyling();
  };

  const setLineStyle = (nextLineStyle: RouteLineStyle) => {
    currentLineStyle = nextLineStyle;
    strokeIcons = buildStrokeOptions(currentLineStyle).icons;
    applyRouteStyling();
  };


  // pins carry their point index so the draw-in can reveal them as the
  // route reaches them instead of all at once
  type DetailPin = {
    marker: any;
    pointIndex: number;
    shown: boolean;
    // kept so the pin can be re-iconed at a different size without having
    // to go back to the trip to find out what event it is
    point: VehicleMapDataPoint;
    focus: EventPinFocus;
    // when this pin stands for a run of identical consecutive events, the
    // events it stands for (itself last) and the pill showing how long the
    // run lasted. Absent on an ordinary one-event pin.
    runPoints?: VehicleMapDataPoint[];
    runLabelMarker?: any;
    runLabel?: string;
  };
  let detailPins: DetailPin[] = [];

  // --- which event pin is being pointed at, and which one was picked.
  // Events pile up on the same few metres of road, so without this there is
  // no way to tell which of a stack the pointer is on, or which one the
  // open detail panel is describing. Mirrors the multi-vehicle renderer.
  const selectedPulse = createSelectedEventPulse(map);
  let hoveredPin: DetailPin | null = null;
  let selectedPin: DetailPin | null = null;

  // The duration pill of a collapsed run is a second marker riding beside
  // its pin, so everything that shows or hides the pin has to move both.
  const setPinOnMap = (pin: DetailPin, on: boolean) => {
    pin.marker.setMap(on ? map : null);
    pin.runLabelMarker?.setMap(on ? map : null);
  };

  const applyPinFocus = (pin: DetailPin, focus: EventPinFocus) => {
    if (pin.focus === focus) return;
    pin.focus = focus;
    pin.marker.setIcon(eventMarkerIcon(pin.point, focus));
    pin.marker.setZIndex(PIN_Z_INDEX_BY_FOCUS[focus]);
    // the pill belongs to this pin, so it steps out as the pin grows and
    // comes forward with it rather than being left under the stack
    if (pin.runLabelMarker) {
      pin.runLabelMarker.setIcon(
        runLabelIcon(pin.runLabel ?? "", focus)
      );
      pin.runLabelMarker.setZIndex(PIN_Z_INDEX_BY_FOCUS[focus]);
    }
  };

  // The picked event has to be the one you see, and always drawing it as
  // its own marker is the only way to guarantee that.
  //
  
  let selectedOverlayMarker: any = null;
  const showSelectedOverlay = (point: VehicleMapDataPoint) => {
    const position = { lat: point.latitude, lng: point.longitude };
    if (!selectedOverlayMarker) {
      selectedOverlayMarker = new google.maps.Marker({
        position,
        map,
        // the pin underneath stays the thing you interact with
        clickable: false,
        optimized: false,
        icon: eventMarkerIcon(point, "selected"),
        zIndex: PIN_Z_INDEX_BY_FOCUS.selected,
      });
      return;
    }
    selectedOverlayMarker.setPosition(position);
    selectedOverlayMarker.setIcon(eventMarkerIcon(point, "selected"));
    selectedOverlayMarker.setMap(map);
  };
  const hideSelectedOverlay = () => selectedOverlayMarker?.setMap(null);

  // selection outranks hover, so a pin that is both stays at its selected
  // size instead of shrinking when the pointer lands on it
  const refreshPinFocus = (pin: DetailPin | null) => {
    if (!pin) return;
    applyPinFocus(
      pin,
      pin === selectedPin ? "selected" : pin === hoveredPin ? "hover" : "none"
    );
  };

  const setHoveredPin = (pin: DetailPin | null) => {
    if (hoveredPin === pin) return;
    const previous = hoveredPin;
    hoveredPin = pin;
    refreshPinFocus(previous);
    refreshPinFocus(pin);
  };



  // the open trip is being torn down - drop the focus state with it, or it
  // would point at markers that are no longer on the map
  const clearPinFocus = () => {
    hoveredPin = null;
    selectedPin = null;
    selectedPulse.hide();
    hideSelectedOverlay();
    // the open list addresses pins that are about to be discarded
    eventStacks.closeList();
  };

  const setSelectedPin = (pin: DetailPin | null) => {
    if (selectedPin === pin) return;
    const previous = selectedPin;
    selectedPin = pin;
    refreshPinFocus(previous);
    refreshPinFocus(pin);
  };

  // Selection is held as a point, not a pin.
  const findPinForPoint = (point: VehicleMapDataPoint) =>
    detailPins.find((pin) =>
      pin.point.recordid != null && point.recordid != null
        ? pin.point.recordid === point.recordid
        : pin.point.timestamp === point.timestamp
    ) ?? null;

  const setSelectedEvent = (point: VehicleMapDataPoint | null) => {
    const pin = point ? findPinForPoint(point) : null;
    setSelectedPin(pin);
    if (!point) {
      selectedPulse.hide();
      hideSelectedOverlay();
      return;
    }
    selectedPulse.showAt(point);
    showSelectedOverlay(point);
  };

  // Drives the highlight from whatever the page says is selected, so the
  // event list and the map agree on which event is open. Only a pick
  // reaches here, so the camera move is click-only - see eventCamera.
  const eventCamera = createEventCamera(map);

  const highlightEventPoint = (point: VehicleMapDataPoint | null) => {
    setSelectedEvent(point);
    // clearing the highlight is the event popup closing, which is the cue
    // to put the view back where focusing the first event found it
    if (point) eventCamera.focusOn(point);
    else eventCamera.restore();
  };

  // Hovering a pin with others underneath opens the list of what is there.
  
  const eventStacks = createEventStackController<DetailPin>({
    google,
    map,
    timezone,
    getPins: () => detailPins,
    isPinVisible: (pin) => pin.shown,
    labelFor: eventLabel,
    onHoverPoint: (point) => setHoveredPin(point ? findPinForPoint(point) : null),
    onSelectPoint: (point) => {
      if (!detailModeTripKey) return;
      setSelectedEvent(point);
      onTripPointClick?.(detailModeTripKey, point, "pin");
    },
  });
  // while this doesn't match the open trip, pins stay hidden and the
  // animation reveals them. keeps the event filter populating right after
  // a trip opens from dumping every pin on before the route has drawn.
  let detailAnimationCompleteForKey: string | null = null;

  // a null filteredStatusCodes means no filter applied yet, so show
  // everything; an empty array is a real "show nothing"
  // in-flight chunked builds are abandoned when a new selection arrives
  let detailPinBuildToken = 0;
  // set once the pins are meant to be on the map - a chunked build that is
  // still running has to honour a reveal that already happened
  let detailPinsRevealed = false;
  // whether the open selection is being handled in heavy mode. Drives the
  // ordering: heavy trips keep every pin off the map until the route has
  // finished drawing, then wave them in, so the line is never drawn
  // underneath a map already covered in pins.
  let detailPinsHeavy = false;

  const setDetailedTrip = (
    tripKey: string | null,
    filteredStatusCodes: StatusCodeFilter,
    showPinsImmediately = false
  ) => {
    // a trip opening or closing frames its own route, so a view saved
    // before that is no longer anywhere to go back to
    eventCamera.forget();
    if (tripKey !== detailModeTripKey) detailAnimationCompleteForKey = null;
    detailModeTripKey = tripKey;
    // detail mode changes dimming and interactivity, and the highlight
    // effect runs before this one, so re-apply here
    applyRouteStyling();

    // abandons any chunked build still running for the previous selection
    detailPinBuildToken += 1;
    const buildToken = detailPinBuildToken;

    // the markers these point at are about to be discarded
    clearPinFocus();
    detailPins.forEach((pin) => setPinOnMap(pin, false));
    detailPins = [];

    // nothing to disambiguate in a single-trip view, so hide the badges
    clusterMarkers.forEach((marker) => marker.setMap(tripKey ? null : map));
    if (tripKey) closeLens();

    if (!tripKey) return;
    const entry = entryByTripKey.get(tripKey);
    if (!entry) return;

    const pinnable = entry.trip.points
      .map((point, pointIndex) => ({ point, pointIndex }))
      .filter(({ point }) =>
        statusCodeIsPinned(filteredStatusCodes, point.statuscode)
      );

    // One marker per run of identical consecutive events instead of one
    // per event: a three-minute stop arrives as thirty-five "Stopped"
    // reports from the same spot, and thirty-five stacked pins say nothing
    // the one at the end of them doesn't. This is what stops a busy trip
    // being unreadable - and it is a real reduction in markers, not just
    // hidden ones.
    // every driving event stays its own marker - they are what carry the
    // direction arrows along the route
    const runs = groupIntoRuns(pinnable, (point) => !isMovingEvent(point));

    detailPinsHeavy = runs.length > HEAVY_TRIP_PIN_COUNT;

    // hold pins back while the route is still drawing, exactly as the
    // multi-vehicle renderer does - the draw reveals them on its own clock
    // as the line reaches them. Once the route has already been drawn (a
    // filter change, say) there is nothing to wait for.
    const holdForAnimation =
      !showPinsImmediately && detailAnimationCompleteForKey !== tripKey;
    detailPinsRevealed = !holdForAnimation;

    const addPin = (
      run: (typeof pinnable)[number][],
      { visible, drop }: { visible: boolean; drop: boolean }
    ) => {
      // the last of the run carries the marker - Matt's "a stopped icon at
      // the last one" - so the pin sits where the asset actually was when
      // it moved off again
      const { point, pointIndex } = run[run.length - 1];
      const marker = new google.maps.Marker({
        position: { lat: point.latitude, lng: point.longitude },
        map: visible ? map : null,
        icon: eventMarkerIcon(point),
        zIndex: PIN_Z_INDEX_BY_FOCUS.none,
        // quick native pop-in instead of the pins just appearing instantly
        ...(drop ? { animation: google.maps.Animation.DROP } : {}),
      });
      const pin: DetailPin = {
        marker,
        pointIndex,
        shown: visible,
        point,
        focus: "none",
      };

      if (run.length > 1) {
        pin.runPoints = run.map((item) => item.point);
        // to the next thing that happened, not to the last identical
        // report - the asset was still sitting there until it moved
        const after = entry.trip.points[pointIndex + 1];
        const endsAt = after ? after.timestamp : point.timestamp;
        pin.runLabel = formatDuration(endsAt - run[0].point.timestamp);
        pin.runLabelMarker = new google.maps.Marker({
          position: { lat: point.latitude, lng: point.longitude },
          map: visible ? map : null,
          clickable: false,
          icon: runLabelIcon(pin.runLabel),
          zIndex: PIN_Z_INDEX_BY_FOCUS.none,
        });
      }
      
      marker.addListener("mouseover", () => {
        setHoveredPin(pin);
        // a pin standing for a run of identical events offers what it
        // stands for before anything about the map around it
        if (pin.runPoints && eventStacks.openRun(pin.runPoints)) return;
        
        eventStacks.openStackFor(pin);
      });
      marker.addListener("mouseout", () => {
        if (hoveredPin === pin) setHoveredPin(null);
        eventStacks.scheduleClose();
      });
      marker.addListener("click", () => {
        // marked straight away rather than waiting for the page to come
        // back with its selection - the pulse should be up by the time the
        // detail panel is
        setSelectedEvent(pin.point);
        onTripPointClick?.(tripKey, point, "pin");
      });
      detailPins.push(pin);
    };

    // the ordinary case, unchanged: one pass, every pin dropping in together
    if (!detailPinsHeavy) {
      runs.forEach((run) =>
        addPin(run, { visible: !holdForAnimation, drop: !holdForAnimation })
      );
        return;
    }

    // Heavy trip. Constructing thousands of markers in one pass blocks the
    // main thread long enough to stall the route animation that is supposed
    // to be playing, so the build is spread a chunk per frame and runs
    // alongside the draw. Pins start hidden whatever the caller asked for -
    // whichever reveal applies brings them in afterwards.
    let buildIndex = 0;
    const buildChunk = () => {
      if (buildToken !== detailPinBuildToken) return;
      const end = Math.min(buildIndex + PIN_BUILD_CHUNK, runs.length);
      for (; buildIndex < end; buildIndex++) {
        // a reveal may already have run past this pin while the build was
        // still catching up, so honour it rather than leaving a hole
        addPin(runs[buildIndex], {
          visible: detailPinsRevealed,
          drop: false,
        });
      }
      if (buildIndex < runs.length) {
        requestAnimationFrame(buildChunk);
        return;
      }
      // the detail animation reveals pins on its own clock as the route
      // reaches them; only take over when nothing else is going to
      if (!holdForAnimation) revealDetailPinsInWaves(buildToken);
    };
    requestAnimationFrame(buildChunk);
  };

  // Brings pins in a chunk per frame, each chunk still doing the native drop
  // bounce. Keeps the entrance a heavy trip would otherwise have to go
  // without, at a few dozen concurrent animations instead of thousands.
  const revealDetailPinsInWaves = (buildToken: number) => {
    let cursor = 0;
    const step = () => {
      if (buildToken !== detailPinBuildToken) return;
      const end = Math.min(cursor + PIN_REVEAL_CHUNK, detailPins.length);
      for (; cursor < end; cursor++) {
        const pin = detailPins[cursor];
        if (pin.shown) continue;
        setPinOnMap(pin, true);
        pin.marker.setAnimation(google.maps.Animation.DROP);
        pin.shown = true;
      }
      if (cursor < detailPins.length) requestAnimationFrame(step);
      else detailPinsRevealed = true;
    };
    requestAnimationFrame(step);
  };

  const showAllDetailPins = () => {
    // a chunked build may still be running - this tells it to create the
    // rest already on the map rather than hidden
    detailPinsRevealed = true;
    detailPins.forEach((pin) => {
      if (pin.shown) return;
      setPinOnMap(pin, true);
      pin.shown = true;
    });
  };

  const fitBoundsToTrip = (tripKey: string): Promise<void> => {
    const entry = entryByTripKey.get(tripKey);
    if (!entry || entry.rawPath.length === 0) return Promise.resolve();
    const bounds = new google.maps.LatLngBounds();
    entry.rawPath.forEach((point: any) => bounds.extend(point));
    return new Promise((resolve) => {
      google.maps.event.addListenerOnce(map, "idle", resolve);
      map.fitBounds(bounds, 60);
    });
  };

  const fitBoundsToTrips = (tripKeys: string[]): Promise<void> => {
    const bounds = new google.maps.LatLngBounds();
    let hasAny = false;
    tripKeys.forEach((tripKey) => {
      const entry = entryByTripKey.get(tripKey);
      if (!entry) return;
      entry.rawPath.forEach((point: any) => {
        bounds.extend(point);
        hasAny = true;
      });
    });
    if (!hasAny) return Promise.resolve();
    return new Promise((resolve) => {
      google.maps.event.addListenerOnce(map, "idle", resolve);
      map.fitBounds(bounds, 60);
    });
  };

  // --- overlap clusters. detected up front, but the badges aren't added
  // until the draw-in finishes (see playDrawInAnimation).
  // On by default here, unlike the multi-vehicle map: this view is one
  // asset over at most 7 days, so detection is cheap and days retracing the
  // same route is exactly what you came to see.
  let overlapsEnabled = true;

  // Capping the span matters more here than on the fleet map: every trip
  // belongs to the SAME asset, and an asset that runs the same route daily
  // shares that route with itself the whole way along. Uncapped, a week
  // collapses into a single number on one point that says nothing about
  // where. A capped span breaks the corridor into a run of badges, each
  // counting the trips on its own stretch, so the count visibly drops
  // wherever the days diverged - and taking the cap from the zoom means the
  // run gets finer as you look closer.
  //
  // the zoom the badges on screen were worked out for. Detection walks every
  // metre of every route, so it is not re-run on each wheel click - only
  // when the integer zoom lands somewhere new, which is the only thing that
  // changes the answer.
  let clusterZoomLevel: number | null = null;

  const computeClusters = () => {
    if (!overlapsEnabled) return [];
    const zoom = Math.round(map.getZoom() ?? 12);
    clusterZoomLevel = zoom;
    return detectOverlapClusters(
      entries.map((entry) => ({
        tripKey: entry.tripKey,
        path: entry.rawPath,
      })),
      {
        maxSpanMeters: overlapSpanForZoom(
          zoom,
          map.getCenter()?.lat?.() ?? 0
        ),
      }
    );
  };

  let clusters = computeClusters();

  // the top-bar toggle owns this - a styling/visibility change, not a redraw
  const setOverlapsEnabled = (enabled: boolean) => {
    if (overlapsEnabled === enabled) return;
    overlapsEnabled = enabled;
    if (!enabled) closeLens();
    clusters = computeClusters();
    if (!detailModeTripKey) createClusterMarkers();
  };

  const clusterTripInfo = (cluster: OverlapCluster): LensTrip[] =>
    cluster.tripKeys
      .map((tripKey) => entryByTripKey.get(tripKey))
      .filter((entry): entry is (typeof entries)[number] => !!entry)
      .map((entry) => ({
        tripKey: entry.tripKey,
        dayLabel: entry.trip.dayLabel,
        tripNumber: entry.trip.tripNumber,
        color: entry.color,
        rawPath: entry.rawPath,
      }));

  let clusterMarkers: any[] = [];
  // The two halves of looking at a badge, and they are deliberately not the
  // same thing. The list says which trips are here and is reached by
  // hovering; the lens pulls the routes apart so you can see them and is
  // reached by clicking. Clicking doesn't take the list away - it is the
  // same badge being looked at more closely. Same split as the
  // multi-vehicle map.
  let activeClusterList: any | null = null;
  let activeClusterListId: string | null = null;
  let activeClusterLens: any | null = null;

  const closeClusterList = () => {
    activeClusterList?.setMap(null);
    activeClusterList = null;
    activeClusterListId = null;
  };

  const closeClusterLens = () => {
    activeClusterLens?.setMap(null);
    activeClusterLens = null;
  };

  const closeLens = () => {
    closeClusterList();
    closeClusterLens();
  };

  // A day-grouped, scrollable list: the lens alone was fine at two or three
  // trips, but a week of daily runs puts dozens under one badge and no
  // amount of nudging makes that clickable. Grouped by day it stays
  // readable at any count, and it's the same popup the multi-vehicle map
  // and the fleet map already use. The lens below complements it.
  const openClusterList = (cluster: OverlapCluster) => {
    // re-entering the same badge shouldn't rebuild the popup underneath the
    // cursor - it would throw away a scroll position and any drag
    if (activeClusterListId === cluster.id) return;
    closeClusterList();
    activeClusterListId = cluster.id;
    const info = clusterTripInfo(cluster);
    const dayCount = new Set(info.map((item) => item.dayLabel)).size;
    const overlay = createClusterAssetListOverlay({
      google,
      map,
      position: { lat: cluster.lat, lng: cluster.lng },
      trips: info.map((item) => ({
        tripKey: item.tripKey,
        // the grouping key - one asset, so days are what differ here
        vehicleLabel: item.dayLabel,
        tripNumber: item.tripNumber,
        color: item.color,
      })),
      heading: `${info.length} trip${info.length === 1 ? "" : "s"} across ${dayCount} day${dayCount === 1 ? "" : "s"}`,
      groupRows: true,
      onViewDetails: (tripKey) => {
        const entry = entryByTripKey.get(tripKey);
        closeLens();
        if (entry) onTripPointClick?.(tripKey, entry.trip.points[0]);
      },
      // picking a row out of the list highlights that route behind it, so
      // you can see which one you're about to open
      onHoverTrip: (tripKey) => setHighlightedTrip(tripKey),
      onClose: closeLens,
    });
    overlay.setMap(map);
    activeClusterList = overlay;
  };

  // The magnifier: a zoomed-in map of this one spot with the routes nudged
  // apart, which is the only way to tell which line is which where several
  // days run down the same road. The nudge exists only inside the circle -
  // the map underneath is never altered. The list beside it already names
  // the trips, so here it is the circle on its own.
  const openClusterLens = (cluster: OverlapCluster) => {
    closeClusterLens();
    const overlay = createClusterLensOverlay({
      google,
      mainMap: map,
      position: { lat: cluster.lat, lng: cluster.lng },
      showPanel: false,
      trips: clusterTripInfo(cluster),
      onTripClick: (tripKey) => {
        const entry = entryByTripKey.get(tripKey);
        closeLens();
        if (entry) onTripPointClick?.(tripKey, entry.trip.points[0]);
      },
      onClose: closeClusterLens,
    });
    overlay.setMap(map);
    activeClusterLens = overlay;
  };

  // Going closer, rather than framing every trip under the badge. Those
  // trips may each run the width of the city, and fitting all of them is the
  // opposite of looking at the one place where they meet. Two levels in is
  // also what splits the badge into the ones it was standing for.
  const zoomToCluster = (cluster: OverlapCluster) => {
    map.panTo({ lat: cluster.lat, lng: cluster.lng });
    map.setZoom(Math.min(18, (map.getZoom() ?? 12) + 2));
  };

  const backgroundClickListener = map.addListener("click", () => {
    closeLens();
    eventStacks.closeList();
  });

  // What counts as one place changes with the zoom, so the badges are worked
  // out again once the map settles somewhere new. Idle rather than
  // zoom_changed, so a pinch or a double-click costs one pass instead of one
  // per frame, and panning costs none at all.
  const overlapZoomListener = map.addListener("idle", () => {
    if (!overlapsEnabled || isAnimating) return;
    if (Math.round(map.getZoom() ?? 12) === clusterZoomLevel) return;
    clusters = computeClusters();
    if (!detailModeTripKey) createClusterMarkers();
  });

  const createClusterMarkers = () => {
    // clears any badges from a previous run (e.g. "Replay All" re-triggers
    // this same animation) before creating a fresh set
    clusterMarkers.forEach((marker) => marker.setMap(null));
    clusterMarkers = clusters.map((cluster) => {
      const marker = new google.maps.Marker({
        position: { lat: cluster.lat, lng: cluster.lng },
        // stay hidden if a trip is currently open in detail view - they're
        // restored by setDetailedTrip(null) on the way back out
        map: detailModeTripKey ? null : map,
        icon: clusterBadgeIcon(google, cluster.tripKeys.length),
        zIndex: 200,
      });

      // Hovering puts the list up and leaves it up - the same as the fleet
      // map's asset clusters and the multi-vehicle map's overlaps, which is
      // where people have already learnt this. A tooltip that died with the
      // cursor couldn't be read, let alone moved into and clicked.
      marker.addListener("mouseover", () => openClusterList(cluster));
      marker.addListener("click", (e: any) => {
        e?.domEvent?.stopPropagation?.();
        openClusterList(cluster);
        openClusterLens(cluster);
        zoomToCluster(cluster);
      });

      return marker;
    });
  };

  const arrowHeadIcon = (color: string) => ({
    icon: {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 3,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: color,
    },
    offset: "100%",
  });

 
  type TimelinePace = { steps: number; durationMs: number };
  // the reveal on load is brisk - it's only showing you what's there
  const INITIAL_PACE: TimelinePace = { steps: 140, durationMs: 6000 };
  // Replay All is the one you sit and watch: 90s at 1x, and the speed
  // buttons divide it, so 10x still brings it back to about nine seconds.
  const REPLAY_PACE: TimelinePace = { steps: 450, durationMs: 90000 };

  let animationToken = 0;

  const timelineFor = (forEntries: typeof entries) => {
    const timestamps: number[] = [];
    forEntries.forEach(({ animTimestamps }) =>
      timestamps.push(...animTimestamps)
    );
    return buildReplayTimeline(timestamps);
  };

  // one draw for both the load reveal and Replay All - they differ only in
  // pace and in whether anything is listening to the clock
  const runTimelineAnimation = async (
    getSpeedMultiplier: () => number,
    // clock is the real unix timestamp the draw is currently at. fastForward
    // is true while it is racing through a stretch nothing was reported in -
    // see replayTimeline.
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean
    ) => void,
    pace: TimelinePace = INITIAL_PACE,
    
    resetDetailPins = true
  ) => {
    const { steps: timelineSteps, durationMs } = pace;
    animationToken += 1;
    const token = animationToken;
    releaseReplayPause();

    // badges reappear only once this run finishes
    clusterMarkers.forEach((marker) => marker.setMap(null));
    clusterMarkers = [];
    closeLens();
    // blank everything first, or already-drawn trips stay visible and it
    // looks like the replay is drawing on top of them
    entries.forEach(({ polyline }) => polyline.setPath([]));
    if (resetDetailPins) {
      clearPinFocus();
      detailPins.forEach((pin) => setPinOnMap(pin, false));
      detailPins = [];
    }

    const timeline = timelineFor(entries);
    if (!timeline) {
      entries.forEach(({ polyline, rawPath }) => polyline.setPath(rawPath));
      createClusterMarkers();
      return;
    }

    // the clock only moves forward, so each route resumes scanning where it
    // stopped instead of walking its timestamps again every frame
    const drawnCounts = entries.map(() => 0);
    // the arrowhead is set on a route when it starts moving and cleared when
    // it stops - re-setting icons every frame is the expensive part
    const hasArrow = entries.map(() => false);

    for (let step = 1; step <= timelineSteps; step++) {
      if (token !== animationToken || cancelled) return;
      const { clock, isFastForward } = timeline.at(step / timelineSteps);

      entries.forEach(
        ({ polyline, animPath, animTimestamps, color }, entryIndex) => {
          let count = drawnCounts[entryIndex];
          while (count < animTimestamps.length && animTimestamps[count] <= clock)
            count += 1;

          // at this pace most routes stand still on most frames - redrawing
          // an unchanged path is what costs, so don't
          if (count === drawnCounts[entryIndex]) {
            // it stopped moving: drop the arrowhead so only the route
            // currently being driven carries one
            if (hasArrow[entryIndex]) {
              hasArrow[entryIndex] = false;
              polyline.setOptions({ icons: strokeIcons });
            }
            return;
          }
          drawnCounts[entryIndex] = count;

          if (count < 2) {
            polyline.setPath([]);
            return;
          }
          polyline.setPath(animPath.slice(0, count));
          if (!hasArrow[entryIndex]) {
            hasArrow[entryIndex] = true;
            polyline.setOptions({ icons: [...strokeIcons, arrowHeadIcon(color)] });
          }
        }
      );

      onProgress?.(
        Math.round((step / timelineSteps) * 100),
        clock,
        isFastForward
      );
      await wait(durationMs / timelineSteps / getSpeedMultiplier());
      await waitIfReplayPaused();
      if (token !== animationToken || cancelled) return;
    }

    if (token !== animationToken || cancelled) return;
    // the thinned copy got it here; the finished route is the real path
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    createClusterMarkers();
  };

  // try/finally rather than clearing the flag at each `return`: the body
  // bails out in several places, and a missed one would strand the flag set
  // and silently stop every later path upgrade from ever being applied.
  const playTimelineAnimation = async (
    ...args: Parameters<typeof runTimelineAnimation>
  ) => {
    isAnimating = true;
    try {
      return await runTimelineAnimation(...args);
    } finally {
      isAnimating = false;
      flushPendingPathUpgrades();
    }
  };

  // The reveal on load. It takes a speed multiplier and reports progress
  // like every other animation here, so the controls panel can sit over it
  // and the speed buttons reach it - the multi-vehicle map drives its own
  // initial reveal exactly this way. Defaults to a plain 1x for a caller
  // that just wants the routes to appear.
  const playDrawInAnimation = (
    getSpeedMultiplier: () => number = () => 1,
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean
    ) => void
  ) =>
    playTimelineAnimation(getSpeedMultiplier, onProgress, INITIAL_PACE, false);

  const playTimelineReplay = (
    getSpeedMultiplier: () => number,
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean
    ) => void
  ) => playTimelineAnimation(getSpeedMultiplier, onProgress, REPLAY_PACE);

 
  const DETAIL_STEPS = 160;
  const DETAIL_DURATION_MS = 40000;
  // pins trail the drawing head by this many steps, not by real time - a
  // fixed visual lag regardless of how long the trip spanned, so pins on a
  // two-hour trip don't wait real minutes to appear
  const DETAIL_PIN_LAG_STEPS = 6;
  let detailAnimationToken = 0;

  const runDetailAnimation = async (
    tripKey: string,
    getSpeedMultiplier: () => number,
    // clock is the real unix timestamp at the drawing head - the head only
    // ever sits on a reported fix, so this is a measured time, not an
    // interpolated one.
    // speedKph is what the asset was reporting at that same fix. One trip
    // is one asset, so unlike the whole-map draws there is a single
    // unambiguous speed to show here.
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean,
      speedKph?: number
    ) => void
  ) => {
    const entry = entryByTripKey.get(tripKey);
    if (!entry) return;
    // the token check in the loop abandons any previous run
    detailAnimationToken += 1;
    const token = detailAnimationToken;
    releaseReplayPause();

    const { polyline, rawPath, animPath, animTimestamps, color, trip } = entry;
    polyline.setPath([]);

    const timeline = buildReplayTimeline(animTimestamps);
    if (!timeline) {
      // no real span to pace against - just put the finished picture up
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
      const lastPoint = trip.points[trip.points.length - 1];
      onProgress?.(100, lastPoint?.timestamp, false, lastPoint?.speedkph);
      showAllDetailPins();
      detailAnimationCompleteForKey = tripKey;
      return;
    }

    polyline.setOptions({ icons: [...strokeIcons, arrowHeadIcon(color)] });
    // resumes scanning where it left off rather than rewalking the
    // timestamps every frame
    let drawnCount = 0;
    // the reported fix the clock has reached, walked forward the same way -
    // the drawn path may be road-aligned geometry, so the speed has to come
    // off the fixes themselves rather than off the line
    let speedCursor = 0;

    for (let step = 1; step <= DETAIL_STEPS; step++) {
      if (token !== detailAnimationToken || cancelled) return;
      const { clock, isFastForward } = timeline.at(step / DETAIL_STEPS);
      // pins lag a fixed few steps behind the line, in animation time
      const { clock: lagClock } = timeline.at(
        Math.max(0, step - DETAIL_PIN_LAG_STEPS) / DETAIL_STEPS
      );

      while (drawnCount < animTimestamps.length && animTimestamps[drawnCount] <= clock)
        drawnCount += 1;
      if (drawnCount >= 2) polyline.setPath(animPath.slice(0, drawnCount));

      // reveal pins whose own reported time the (lagged) clock has reached -
      // real time, the same as the line drawing through them.
      // Capped per step: a long stop can report hundreds of events within
      // one step's slice of the clock, and animating them together is the
      // same stall this pacing exists to avoid. The overflow still satisfies
      // the check next step, so it catches up a chunk at a time.
      let revealedThisStep = 0;
      for (const pin of detailPins) {
        if (revealedThisStep >= PIN_REVEAL_CHUNK) break;
        if (pin.shown) continue;
        const point = trip.points[pin.pointIndex];
        if (!point || point.timestamp > lagClock) continue;
        setPinOnMap(pin, true);
        pin.marker.setAnimation(google.maps.Animation.DROP);
        pin.shown = true;
        revealedThisStep += 1;
      }

      while (
        speedCursor + 1 < trip.points.length &&
        trip.points[speedCursor + 1].timestamp <= clock
      )
        speedCursor += 1;

      onProgress?.(
        Math.round((step / DETAIL_STEPS) * 100),
        clock,
        isFastForward,
        trip.points[speedCursor]?.speedkph
      );
      await wait(DETAIL_DURATION_MS / DETAIL_STEPS / getSpeedMultiplier());
      await waitIfReplayPaused();
      if (token !== detailAnimationToken || cancelled) return;
    }

    if (token !== detailAnimationToken || cancelled) return;
    // the thinned copy got it here; the finished route is the real path
    polyline.setPath(rawPath);
    polyline.setOptions({ icons: strokeIcons });
    const finalPoint = trip.points[trip.points.length - 1];
    onProgress?.(100, finalPoint?.timestamp, false, finalPoint?.speedkph);

    // the tail end that was still inside the lag window
    showAllDetailPins();
    detailAnimationCompleteForKey = tripKey;
  };


  // same guard as the timeline draw - see the note there
  const playDetailAnimation = async (
    ...args: Parameters<typeof runDetailAnimation>
  ) => {
    isAnimating = true;
    try {
      return await runDetailAnimation(...args);
    } finally {
      isAnimating = false;
      flushPendingPathUpgrades();
    }
  };

  // undoes showAllDetailPins/stopDetailAnimation's "everything shown" -
  // hides every pin and forgets that the draw already completed, so the
  // next playDetailAnimation does its real reveal-as-the-line-passes
  // animation again instead of finding every pin already marked shown and
  // leaving them sitting there over the redrawing route. This is what
  // gives the detail view's Replay button something to replay.
  const resetDetailPinsForReplay = () => {
    detailPins.forEach((pin) => {
      pin.shown = false;
      setPinOnMap(pin, false);
    });
    detailAnimationCompleteForKey = null;
    clearPinFocus();
  };

  // snaps the route to complete and drops any remaining pins
  const stopDetailAnimation = () => {
    detailAnimationToken += 1;
    releaseReplayPause();
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    showAllDetailPins();
    if (detailModeTripKey) detailAnimationCompleteForKey = detailModeTripKey;
  };

  // snaps every trip to its full path rather than leaving one half-drawn
  const stopTimelineReplay = () => {
    // bumping the token abandons whichever run is in the loop
    animationToken += 1;
    releaseReplayPause();
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    createClusterMarkers();
  };

  return {
    clear: () => {
      cancelled = true;
      animationToken += 1;
      detailAnimationToken += 1;
      releaseReplayPause();
      google.maps.event.removeListener(backgroundClickListener);
      google.maps.event.removeListener(overlapZoomListener);
      closeLens();
      entries.forEach(({ polyline }) => polyline.destroy());
      routeHitLayer.destroy();
      clusterMarkers.forEach((marker) => marker.setMap(null));
      selectedPulse.destroy();
      selectedOverlayMarker?.setMap(null);
      eventStacks.closeList();
      detailPins.forEach((pin) => setPinOnMap(pin, false));
    },
    fitBoundsIfAny: () => {
      if (entries.length === 0) return;
      const bounds = new google.maps.LatLngBounds();
      entries.forEach(({ rawPath }) =>
        rawPath.forEach((point) => bounds.extend(point))
      );
      map.fitBounds(bounds);
    },
    updateTripPath,
    playDrawInAnimation,
    playTimelineReplay,
    stopTimelineReplay,
    playDetailAnimation,
    stopDetailAnimation,
    resetDetailPinsForReplay,
    pauseReplay,
    resumeReplay,
    setHighlightedTrip,
    setLineStyle,
    setDetailedTrip,
    highlightEventPoint,
    setOverlapsEnabled,
    fitBoundsToTrip,
    fitBoundsToTrips,
    closeLens,
  };
};
